import React, { useEffect, useRef, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import ReadMoreOutlined from '@mui/icons-material/ReadMoreOutlined'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import usePlannerAuthorization from '../generic/hooks/useAuthorization/usePlannerAuthorization'
import usePlanMetrics from './usePlanMetrics'
import PlanIndicatorCard, { PlanIndicatorFoldedItem } from './PlanIndicatorCard'

// RN49 (amended after GATE 2): the ribbon is as wide as its block and never wider. Nothing measures the window and nothing
// sticks sideways: the page itself no longer scrolls horizontally (the blocks list is `contain: inline-size`, S212), so the
// block is as wide as the window and the ribbon takes the block's width.
const RibbonBox = ({ children }) => (
  <Box data-testid='plan-indicators-ribbon' sx={{ maxWidth: '100%', minWidth: 0 }}>
    {children}
  </Box>
)

// AP11 (contract §8) -- both decisions live only in this browser, under these frozen keys. Both hold
// exactly 'true' or 'false'; anything else, or nothing, means the default (extra information hidden,
// ribbon unfolded -- D-Z). Reading and saving never fail for the user (POL7), as in `PlantKpiBar`.
const EXTRA_INFO_STORAGE_KEY = 'planner.planIndicators.extraInfoVisible'
const COLLAPSED_STORAGE_KEY = 'planner.planIndicators.collapsed'

const readStoredFlag = (key) => {
  try { return window.localStorage.getItem(key) === 'true' } catch { return false }
}

const storeFlag = (key, value) => {
  try { window.localStorage.setItem(key, String(value)) } catch { /* storage unavailable -- non-fatal */ }
}

// I2/RN5 -- the cinta only ever forwards these five: `deviceGroupId`, `ungrouped` and `deviceId` are
// local filters of the load-capacity block and are never sent from here, even if a caller passes
// them by mistake (S73).
const INDICATOR_PARAM_KEYS = ['fabricationOrderId', 'partReferenceId', 'orderId', 'clientId', 'timeZone']

const toIndicatorParams = (plannerFilters = {}) => INDICATOR_PARAM_KEYS.reduce((params, key) => {
  if (plannerFilters[key] !== null && plannerFilters[key] !== undefined) {
    params[key] = plannerFilters[key]
  }
  return params
}, {})

// V8/E1 -- Sun answers a handful of stable `code`s; only `PLAN_NOT_FOUND` has a dedicated i18n key
// for this ribbon. Anything else falls back to the generic literal (E10/E11).
const ERROR_MESSAGE_KEY_BY_CODE = {
  PLAN_NOT_FOUND: 'Planification not found'
}

// Presentation-only rounding (S81 allows it explicitly): the hours already arrive with one decimal
// (P7), this just keeps the caption's formatting consistent.
const formatHours = (value) => `${Number(value).toFixed(1)} h`

const buildCardDefinitions = (data, t_) => [
  {
    key: 'plannedFabricationOrders',
    title: t_('Planned fabrication orders'),
    value: String(data.plannedFabricationOrders.fabricationOrderCount),
    caption: t_('with jobs in the plan'),
    secondaryCaption: `${formatHours(data.plannedFabricationOrders.firmLoadHours)} + ${formatHours(data.plannedFabricationOrders.forecastLoadHours)} (${t_('firm + forecast')})`,
    status: data.plannedFabricationOrders.status
  },
  {
    key: 'planOccupancy',
    title: t_('Plan occupancy'),
    value: data.planOccupancy.ratioPercent === null ? t_('Not available') : `${data.planOccupancy.ratioPercent}%`,
    caption: t_('load hours over capacity hours'),
    secondaryCaption: `${formatHours(data.planOccupancy.loadHours)} / ${formatHours(data.planOccupancy.capacityHours)}`,
    status: data.planOccupancy.status
  },
  {
    key: 'saturatedMachineWeeks',
    title: t_('Saturated machine weeks'),
    value: String(data.saturatedMachineWeeks.cellCount),
    caption: t_('{{affected}} of {{total}} machines affected', {
      affected: data.saturatedMachineWeeks.affectedDeviceCount,
      total: data.saturatedMachineWeeks.evaluatedDeviceCount
    }),
    status: data.saturatedMachineWeeks.status
  },
  {
    key: 'lateFabricationOrders',
    title: t_('Late fabrication orders'),
    value: String(data.lateFabricationOrders.fabricationOrderCount),
    caption: `${data.lateFabricationOrders.percentOfPlanned}% ${t_('over planned fabrication orders')}`,
    secondaryCaption: `${formatHours(data.lateFabricationOrders.lateLoadHours)} ${t_('Load in delay')}`,
    status: data.lateFabricationOrders.status
  },
  {
    key: 'maxDelay',
    title: t_('Maximum delay'),
    // A4/S77 -- zero and "no OF caused it" travel together, and only together (INV-5).
    value: `${data.maxDelay.days} d`,
    // A fabrication order need not have a client, so a real LATE order can arrive with a code and a
    // null clientName (contract §1). Interpolating it blindly printed the literal "null" beside the
    // code: each part is shown only when it is actually there.
    caption: [data.maxDelay.fabricationOrderCode, data.maxDelay.clientName]
      .filter((part) => part !== null && part !== undefined && part !== '')
      .join(' · ') || null,
    status: data.maxDelay.status
  },
  {
    key: 'accumulatedEarliness',
    title: t_('Accumulated earliness'),
    value: `${data.accumulatedEarliness.days} d`,
    caption: `${data.accumulatedEarliness.fabricationOrderCount} ${t_('fabrication orders above the threshold')}`,
    secondaryCaption: data.fabricationOrdersWithoutTargetDate > 0
      ? `${data.fabricationOrdersWithoutTargetDate} ${t_('Without required date')}`
      : null,
    status: data.accumulatedEarliness.status
  }
]

/**
 * CU7 -- the six-indicator ribbon that lives inside the Timeline block, above the Gantt. Jupiter
 * never computes a single one of these figures (RN44/S81): it only reads `planMetrics/{id}/indicators`
 * and formats, translates and paints what the server already decided, `status` included.
 */
const PlanIndicatorsRibbon = ({ planificationId, plannerFilters = {}, metricsVersion = 0 }) => {
  const { t_ } = useSmartTranslation()
  const isUserPlannerAuthorized = usePlannerAuthorization()
  const isAuthorized = isUserPlannerAuthorized('PLANNER')
  const hasSelectedPlan = planificationId !== null && planificationId !== undefined

  // AP11.2/AP11.4 -- two browser-only decisions, read once when the ribbon opens and saved when pressed.
  const [extraInfoVisible, setExtraInfoVisible] = useState(() => readStoredFlag(EXTRA_INFO_STORAGE_KEY))
  const [collapsed, setCollapsed] = useState(() => readStoredFlag(COLLAPSED_STORAGE_KEY))

  const { data, error, reload } = usePlanMetrics({
    planificationId,
    endpoint: 'indicators',
    params: toIndicatorParams(plannerFilters),
    enabled: isAuthorized && hasSelectedPlan
  })

  // CU9/S100-S102/S104: a Gantt job drop, Publish, Reset, Save, Activate, Plan or Delete can leave
  // `planificationId` and `plannerFilters` unchanged, so the fetch above would not retrigger on its
  // own. `Planner.jsx` bumps `metricsVersion` once each of those succeeds; reusing `usePlanMetrics`'s
  // own debounced `reload` (instead of a second, parallel fetch path) keeps P4's debounce and
  // stale-response discard (by `requestId`) intact for this trigger too. The first render must NOT
  // reload: `usePlanMetrics` already fetches once on mount for the current `metricsVersion`.
  const isFirstMetricsVersionRender = useRef(true)
  useEffect(() => {
    if (isFirstMetricsVersionRender.current) {
      isFirstMetricsVersionRender.current = false
      return
    }
    reload()
  }, [metricsVersion, reload])

  // P2/A1/S78/S80 -- no permission or no selected plan: nothing is requested, nothing is painted.
  if (!isAuthorized || !hasSelectedPlan) {
    return null
  }

  // P3/S79 -- a failed calculation never blocks the Gantt: it paints its own error, with a retry,
  // and never keeps showing figures from a previous, now-stale, answer. Folded or not, an error is
  // never hidden (D-Z.3).
  if (error) {
    // S79 -- "the message shown is chosen by the received code": the specific literal replaces the
    // generic one when the code is known, which is also the repo idiom (PlannerEventDialog).
    // error.message is developer detail and is never shown.
    const specificMessageKey = ERROR_MESSAGE_KEY_BY_CODE[error.data?.code]
    return (
      <RibbonBox>
        <Alert
          severity='error'
          action={<Button color='inherit' size='small' onClick={reload}>{t_('Retry')}</Button>}
          sx={{ marginBottom: '1rem' }}
        >
          <Typography variant='body2'>
            {specificMessageKey ? t_(specificMessageKey) : t_('Plan metrics could not be loaded')}
          </Typography>
        </Alert>
      </RibbonBox>
    )
  }

  if (!data) {
    return null
  }

  const cards = buildCardDefinitions(data, t_)

  const toggleExtraInfo = () => {
    storeFlag(EXTRA_INFO_STORAGE_KEY, !extraInfoVisible)
    setExtraInfoVisible(!extraInfoVisible)
  }
  const toggleCollapsed = () => {
    storeFlag(COLLAPSED_STORAGE_KEY, !collapsed)
    setCollapsed(!collapsed)
  }

  // The translated text is both the tooltip and the accessible name of each button (contract §8).
  const foldLabel = collapsed ? t_('Expand') : t_('Collapse')
  const detailsLabel = extraInfoVisible ? t_('Hide details') : t_('Show details')
  const foldButton = (
    <Tooltip title={foldLabel}>
      <IconButton size='small' aria-label={foldLabel} onClick={toggleCollapsed}>
        {collapsed ? <ExpandMore /> : <ExpandLess />}
      </IconButton>
    </Tooltip>
  )

  // The editing chip is not extra information: it stays with the ribbon folded or not (D-Z.2).
  const editingChip = data.planEditing
    ? (
      <Chip
        label={t_('Editing · {{count}} jobs moved, not published', { count: data.movedJobCount })}
        color='warning'
        size='small'
        data-testid='plan-editing-chip'
        sx={{ alignSelf: 'flex-start' }}
      />
      )
    : null

  return (
    <RibbonBox>
      <Stack spacing={1} sx={{ marginBottom: '1rem' }}>
        {editingChip}
        {collapsed
          ? (
            <Paper variant='outlined' data-testid='plan-indicators-folded' sx={{ px: 2, py: 0.75 }}>
              <Stack direction='row' alignItems='center' gap={3} flexWrap='wrap'>
                {cards.map((card) => (
                  <PlanIndicatorFoldedItem
                    key={card.key}
                    testId={`plan-indicator-${card.key}`}
                    title={card.title}
                    value={card.value}
                    status={card.status}
                  />
                ))}
                <Box sx={{ flexGrow: 1 }} />
                {foldButton}
              </Stack>
            </Paper>
            )
          : (
            <Stack direction='row' gap={2} alignItems='stretch'>
              <Box
                data-testid='plan-indicators-cards'
                sx={{ flex: '1 1 auto', minWidth: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(11rem, 1fr))', gap: 2 }}
              >
                {cards.map((card) => (
                  <PlanIndicatorCard
                    key={card.key}
                    testId={`plan-indicator-${card.key}`}
                    title={card.title}
                    value={card.value}
                    caption={extraInfoVisible ? card.caption : ''}
                    secondaryCaption={extraInfoVisible ? card.secondaryCaption : ''}
                    status={card.status}
                  />
                ))}
              </Box>
              <Stack justifyContent='flex-start'>
                {foldButton}
                <Tooltip title={detailsLabel}>
                  <IconButton
                    size='small'
                    aria-label={detailsLabel}
                    aria-pressed={extraInfoVisible}
                    color={extraInfoVisible ? 'primary' : 'default'}
                    onClick={toggleExtraInfo}
                  >
                    <ReadMoreOutlined />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
            )}
      </Stack>
    </RibbonBox>
  )
}

export default PlanIndicatorsRibbon
